import { useState, useEffect } from "react";
import { Flex, Text, FormControl, Box } from "@chakra-ui/react";
import { FieldError } from "react-hook-form";
import { Input as InputBarte } from "barte-design-system";

interface InputCurrencyBRLProps {
  topLabel?: string;
  leftLabel?: string;
  isTransferTime?: boolean;
  isPercentage?: boolean;
  isLimit?: boolean;
  width?: string;
  height?: string;
  value?: any;
  fontSize?: string;
  isDisabled?: boolean;
  onChangeInput?: (value: string) => void;
  onEdit?: boolean;
  error?: FieldError;
  name?: string;
  placeholder?: string;
  className?: string;
  [key: string]: any;
}

export function InputCurrencyBRL({
  onChangeInput,
  isDisabled,
  topLabel,
  isTransferTime,
  isPercentage,
  isLimit,
  leftLabel,
  height,
  width,
  value,
  fontSize,
  error,
  name,
  placeholder,
  className,
  ...rest
}: InputCurrencyBRLProps) {
  const [formattedValue, setFormattedValue] = useState<string>("");
  const [oldValue, setOldValue] = useState<string>("");
  const [showCurrency, setShowCurrency] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;

    inputValue = inputValue.replace(/[a-zA-Z]/g, "");

    if (isPercentage) {
      inputValue = inputValue.replace("%", "");
      if (Number(inputValue) > 100) {
        inputValue = "100";
      }
    } else if (isLimit) {
      let onlyNumbers = inputValue.replace(/\D/g, "");
      if (onlyNumbers === "") {
        setFormattedValue("R$ 0,00");
        setShowCurrency(true);
        onChangeInput?.("R$ 0,00");
        return;
      }
      if (Number(onlyNumbers) === 0) {
        setFormattedValue("R$ 0,00");
        setShowCurrency(true);
        onChangeInput?.("R$ 0,00");
        return;
      }
      const valueInCents = Number(onlyNumbers);
      const valueInReais = valueInCents / 100;
      let formattedValue = valueInReais.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
      if (valueInCents >= 1000) {
        formattedValue = formattedValue.replace("R$ 0", "R$ ");
      }
      inputValue = formattedValue;
      setShowCurrency(true);
    } else if (isTransferTime) {
      inputValue = inputValue.replace(/[^\d]/g, "");
      setShowCurrency(false);
    } else {
      setShowCurrency(inputValue.replace(/\D/g, '') !== '');
    }

    setFormattedValue(inputValue);
    onChangeInput?.(inputValue);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;
    if (isPercentage && inputValue !== "" && !inputValue.includes("%")) {
      inputValue += "%";
    }
    setFormattedValue(inputValue);
    onChangeInput?.(inputValue);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (isPercentage && e.target.value.includes("%")) {
      e.target.value = e.target.value.replace("%", "");
    }
  };

  const maxLength = () => {
    if (isTransferTime) return 2;
    if (isPercentage) return 5;
    if (isLimit) return 20;
    return undefined;
  };

  useEffect(() => {
    if (isLimit && value >= 0) {
      let newValue = value;
      setOldValue(newValue.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
      }));
    }
    //eslint-disable-next-line
  }, [isLimit]);

  useEffect(() => {
    if (value !== undefined && value !== null) {
      let newValue = value?.toString();
      if (isLimit === true && value >= 0) {
        newValue = newValue.replace("R$", "").replace("%", "").replace(" ", "").replace(",", ".");
        let numericValue = String(value)
          .replace("%", "")
          .replace("R$", "")
          .replace(",", ".")
          .trim();

        let parsedValue = parseFloat(numericValue);

        if (!isNaN(parsedValue)) {
          newValue = parsedValue.toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
            minimumFractionDigits: 2,
          });
          setFormattedValue(newValue);
          setShowCurrency(true);
          onChangeInput?.(newValue);
        }
      } else {
        setFormattedValue(newValue);
        setShowCurrency(newValue.replace(/\D/g, '') !== '');
      }
    }
    //eslint-disable-next-line
  }, [value, isLimit]);

  // Props que o InputBarte aceita
  const inputBarteProps: any = {
    type: "text",
    label: topLabel,
    placeholder: placeholder,
    error: error?.message,
    className: className,
    disabled: isDisabled,
    maxLength: maxLength(),
  };

  // Props de eventos que precisam ser passados para o input interno
  const inputEvents = {
    name: name,
    value: formattedValue,
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    ...rest,
  };

  return (
    <FormControl isInvalid={!!error?.message}>
      <Flex flexDirection="column" w={width || "100%"} h={height}>
        <Flex alignItems="center" alignContent="center" gap="8px" w="100%">
          {leftLabel && (
            <Text color="#8A94A8" fontSize="16px" lineHeight="26px" fontWeight={400}>
              {leftLabel}
            </Text>
          )}
          <Flex flexDir="column" position="relative" flex={1}>
            <Box position="relative" display="inline-block" width="100%">
              <InputBarte
                {...inputBarteProps}
                {...inputEvents}
                style={{
                  paddingLeft: showCurrency && (isLimit || (!isPercentage && !isTransferTime)) ? "30px" : undefined,
                  ...rest?.style,
                }}
              />
             
            </Box>
          </Flex>
        </Flex>
      </Flex>
    </FormControl>
  );
}
